import { Link } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { corDoSdr } from '@/equipe/cores';
import { TEMPERATURAS } from '@/lib/constants';
import { origemPorId } from '@/leads/origens';
import { useLeads } from '@/leads/LeadsProvider';
import { DIAS_ALERTA_RETORNO, diasSemRetorno, mostrarBadgeRetorno } from '@/leads/regras';
import { mostrarBadgeOrcamento } from '@/leads/oportunidade';
import { formatarTelefone } from '@/services/leadsService';
import { aparicaoNaJanela } from '@/leads/resgate';
import { janelaDoPeriodo } from '@/leads/filtros';

/**
 * A etiqueta de CONTATO do card — só para quem compareceu e ainda não fechou (V4).
 *
 * ★★ WE (c) — TRÊS ESTADOS, E A PALAVRA "RETORNO" SAIU.
 *
 * Ela sempre mediu **contato registrado**, nunca "retorno agendado" — não lê tarefa nem consulta. O texto
 * antigo ("retorno hoje" / "sem retorno há N dias") dizia outra coisa, e o card fica ao lado de uma ficha
 * que tem um campo "Próximo contato" com data própria: duas coisas diferentes, o mesmo nome. Decisão do
 * dono na WE: a palavra sai, e os três estados ficam coerentes entre si.
 *
 * ★ O terceiro estado é o que não existia. Antes, sem contato registrado, `diasSemRetorno` caía no
 * `atualizadoEm` e a etiqueta **mentia um número**. Agora ela diz o que é: não há contato registrado.
 * Ela NÃO some — sumir tiraria do radar do negociador os leads mais parados de todos (medido: **8 dos 11**
 * que mostram a etiqueta hoje caem neste estado).
 *
 * ★ E o estado 3 é SEMPRE alerta: "nunca houve contato" é pior que "houve há três dias".
 */
export function BadgeRetorno({ lead, agora }) {
  if (!mostrarBadgeRetorno(lead)) return null;
  const dias = diasSemRetorno(lead, agora);

  if (dias === null) {
    return (
      <span className="badge-retorno alerta" title="Nenhuma tentativa de contato registrada neste lead">
        sem contato registrado
      </span>
    );
  }

  const alerta = dias >= DIAS_ALERTA_RETORNO;
  const texto = dias === 0 ? 'contato hoje' : `sem contato há ${dias} dia${dias === 1 ? '' : 's'}`;
  return (
    <span
      className={alerta ? 'badge-retorno alerta' : 'badge-retorno'}
      title={`Último contato registrado: ${dias === 0 ? 'hoje' : `há ${dias} dia(s)`}`}
    >
      {texto}
    </span>
  );
}

/**
 * "sem orçamento" — quem compareceu e ainda não teve o valor orçado registrado (VE).
 *
 * O comparecimento marcado pela Agenda não pede orçamento (não há diálogo lá), e
 * o do funil aceita em branco de propósito: o orçamento sai depois da avaliação.
 * Este badge é a cobrança — sem ele, o valor simplesmente nunca seria informado,
 * e a conversão de valor nasceria furada.
 */
export function BadgeOrcamento({ lead }) {
  if (!mostrarBadgeOrcamento(lead)) return null;
  return (
    <span
      className="badge-retorno orcamento"
      title="Compareceu e ainda não tem valor orçado — informe na ficha do lead."
    >
      sem orçamento
    </span>
  );
}

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function LeadCard({ lead, aoAbrir }) {
  const { clinica, filtros, leadsComPendencia } = useLeads();
  /**
   * ★★ WO lote 3a — SELO "pendente": há consulta deste lead com data vencida e sem baixa.
   *
   * ⚠ O selo é um CHAMADO À AÇÃO, e por isso conta só o que a EQUIPE agendou: medido, sem
   * o filtro acenderiam 623 dos 1.345 cards (46% da tela), 626 deles da importação — e
   * ninguém dá baixa retroativa em consulta do sistema antigo. A regra mora em
   * `agenda/pendencia.js`; aqui só se desenha.
   */
  const temPendencia = Boolean(leadsComPendencia?.has?.(lead.id));
  // VR: no mês filtrado, um lead de safra anterior que agendou/compareceu/fechou aparece como RESGATE.
  const ehResgate = aparicaoNaJanela(lead, janelaDoPeriodo(filtros?.periodo)) === 'resgate';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });

  const temperatura = TEMPERATURAS.find((t) => t.id === lead.temperatura);

  return (
    <article
      ref={setNodeRef}
      className={isDragging ? 'lead-card arrastando' : 'lead-card'}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      {...listeners}
      {...attributes}
    >
      <div className="lead-card-topo">
        <strong className="lead-nome">{lead.nome || 'Sem nome'}</strong>
        {ehResgate && <span className="pilula-inferido selo-resgate" title="Lead de mês anterior que voltou a agir neste mês">resgate</span>}
        {temPendencia && (
          <Link
            to="/agenda?pendentes=1"
            className="pilula-inferido selo-pendente"
            title="A consulta já passou e ninguém deu baixa (compareceu ou faltou). Não conta na meta."
            onClick={(e) => e.stopPropagation()}
          >
            pendente
          </Link>
        )}
        {temperatura && (
          <span
            className="bolinha"
            style={{ background: temperatura.cor }}
            title={`Temperatura: ${temperatura.label}`}
          />
        )}
      </div>

      {lead.procedimento && (
        <p className="lead-procedimento">
          {lead.procedimento}
          {lead.anuncioInferido === true && <span className="pilula-inferido">inferido</span>}
        </p>
      )}
      {lead.telefone && <p className="lead-meta">{formatarTelefone(lead.telefone)}</p>}
      {/* VQ-3: em Não compareceu por CANCELAMENTO pela agenda, o card diz que não foi falta. */}
      {lead.etapa === 'nao_compareceu' && lead.ultimoCancelamentoEm && (
        <span className="pilula-inferido" title="A última consulta foi cancelada pela agenda — não foi falta">consulta cancelada</span>
      )}
      <BadgeRetorno lead={lead} />
      <BadgeOrcamento lead={lead} />

      <div className="lead-card-rodape">
        <span className="etiqueta">{origemPorId(clinica?.config, lead.origem).label}</span>
        {lead.etapa === 'fechado' && lead.valorFechado ? (
          <span className="valor">{moeda(lead.valorFechado)}</span>
        ) : (
          /* ★ WA — cor por SDR: a mesma pessoa tem sempre a mesma cor (derivada do uid, não da ordem da
             equipe), para reconhecer de relance de quem é o cartão. Lead sem `sdrId` — inclusive o que só
             tem o nome em texto da planilha — fica neutro: cor é identidade de pessoa cadastrada. */
          <span
            className={lead.sdrId ? 'lead-sdr com-cor' : 'lead-sdr'}
            style={{ backgroundColor: corDoSdr(lead.sdrId).fundo, color: corDoSdr(lead.sdrId).texto }}
            title={lead.sdrNome || 'Sem SDR'}
          >
            {lead.sdrNome?.split(' ')[0] ?? '—'}
          </span>
        )}
      </div>

      {/* Botão separado do corpo arrastável: com dnd-kit, um clique dentro da
          área com listeners de drag não vira clique confiável. */}
      <button type="button" className="lead-abrir" onClick={() => aoAbrir(lead.id)}>
        Abrir
      </button>
    </article>
  );
}
